import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import cx from "classnames";

import { SrOnly } from "../../../../../../components";

import { DataTableEditableCellEditIcon } from "./DataTableEditableCellEditIcon";
import { TableBodyCell } from "../../../base/cells/TableBodyCell";

import styles from "./DataTableEditableCells.module.scss";
import dataTableCellStyles from "../cells/DataTableCells.module.scss";
import { formatCellPositionString } from "../util/cellPositionHelpers";
import { EditableCellImperativeHandle, BaseEditableCellProps } from "./types";
import { useDTFocusDispatchContext } from "../context/focus/useDTFocusDispatchContext";
import {
  getColumnId,
  getCellValue,
  getCellOnChange,
  logWarningForInvalidCellValueType,
} from "../util/cellTypeHelpers";
import { DataTableBodyImmutableCell } from "../cells/DataTableBodyImmutableCell";
import { CellFocusContext } from "../cells/CellFocusContext";
import { useNumberField } from "../../../../../../internal/hooks/useNumberField/useNumberField";
import { useMergeRefs } from "../../../../../../hooks/useMergeRefs";

export type DataTableEditableNumberCellProps<T> = BaseEditableCellProps<T>;

/**
 * Renders an editable number cell in a DataTable. This component provides a number input
 * that appears when the cell is activated, leveraging the useNumberField hook for formatting,
 * validation, and keyboard navigation (arrow keys for increment/decrement).
 *
 * Features:
 * - Uses useNumberField hook for precise decimal arithmetic and input formatting
 * - Supports configurable min/max values, step size, and fraction digits
 * - Keyboard navigation: Enter/F2 to commit, Escape to cancel
 * - Input masking with thousand separators and decimal precision
 *
 * @param props - The props for the DataTableEditableNumberCell component
 * @param props.cell - The TanStack table cell instance
 * @param props.cellProps - Props for styling and event handling of the cell container
 * @param props.cellPosition - The position of the cell in the table
 * @param props.children - The content to display when not editing
 * @param props.isHovered - Whether the cell is currently hovered
 * @param ref - Ref to the imperative handle for programmatic control
 * @returns The rendered editable number cell component
 */
function DataTableEditableNumberCellInner<T>(
  props: DataTableEditableNumberCellProps<T>,
  ref: React.Ref<EditableCellImperativeHandle>,
) {
  const { cell, cellProps, cellPosition, children } = props;
  const { onKeyDown: onCellKeyDown } = cellProps;
  const cellRef = useRef<HTMLTableCellElement>(null);
  const localInputRef = useRef<HTMLInputElement>(null);
  const rowId = cell.row.id;
  const [isEditing, setIsEditing] = useState(false);

  // Use type-safe helpers to get column ID and value
  const columnId = getColumnId(cell);
  const cellValue = getCellValue(cell, columnId);

  const [initialValue, isValidValueType] = useMemo(() => {
    let value: number | null;
    let isValid = true;

    if (cellValue === undefined || cellValue === null) {
      value = null;
      isValid = true;
      return [value, isValid];
    }

    if (typeof cellValue === "number") {
      value = cellValue;
    } else {
      value = null;
      isValid = false;
    }

    if (!isValid) {
      logWarningForInvalidCellValueType({
        columnId: String(columnId),
        editMode: "number",
        expectedType: "number",
        receivedType: Array.isArray(cellValue) ? "array" : typeof cellValue,
      });
    }
    return [value, isValid];
  }, [cellValue, columnId]);

  const editConfig = cell.column.columnDef.meta?.editConfig;
  const numberEditOptions = useMemo(() => {
    if (editConfig && editConfig.mode === "number") {
      const { mode: _, onChange: __, ...options } = editConfig;
      return options;
    }
    return {};
  }, [editConfig]);

  const [draftValue, setDraftValue] = useState<number | null>(initialValue);

  const { inputRef: numberFieldInputRef, inputProps } = useNumberField({
    value: draftValue,
    onChange: setDraftValue,
    ...numberEditOptions,
  });

  const combinedInputRef = useMergeRefs([localInputRef, numberFieldInputRef]);

  const [tabIndex, setTabIndex] = useState(-1);
  const { moveFocusToCell } = useDTFocusDispatchContext();

  const instructionsId = useId();
  const headerLabel =
    cell.column.columnDef.meta?.columnDef?.headerLabel ?? String(columnId);
  const inputAriaLabel = `${headerLabel}, row ${cellPosition.rowIndex + 1}`;

  const skipCommitOnBlur = useRef(false);

  const onChange = getCellOnChange(cell, columnId);

  const commitValue = useCallback(() => {
    if (draftValue !== initialValue) {
      // The onChange expects T[K] which should be number for number columns
      onChange?.(draftValue as T[typeof columnId], rowId);
    }
    setIsEditing(false);
  }, [rowId, draftValue, onChange, initialValue]);

  const resetValue = useCallback(() => {
    setDraftValue(initialValue);
    skipCommitOnBlur.current = true;
    cellRef.current?.focus();
    setIsEditing(false);
  }, [initialValue]);

  const beginEditing = useCallback(() => {
    setDraftValue(initialValue);
    setIsEditing(true);
  }, [initialValue]);

  useImperativeHandle(ref, () => ({
    invoke: beginEditing,
    focus: () => {
      cellRef.current?.focus();
      setTabIndex(0);
    },
  }));

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter" || event.key === "F2") {
        event.preventDefault();
        skipCommitOnBlur.current = true;
        cellRef.current?.focus();
        commitValue();
        return;
      } else if (event.key === "Escape") {
        event.preventDefault();
        resetValue();
        return;
      }
    },
    [commitValue, resetValue],
  );

  const handleInputBlur = useCallback(() => {
    if (skipCommitOnBlur.current) {
      skipCommitOnBlur.current = false;
      return;
    }
    commitValue();
  }, [commitValue]);

  const classes = cx(
    styles["data-table-body-cell-editable"],
    cellProps.className,
    {
      [dataTableCellStyles["data-table-body-cell-editing"]]: isEditing,
    },
  );

  const handleCellKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTableCellElement>) => {
      if (isEditing) {
        return;
      }
      onCellKeyDown?.(event);
    },
    [onCellKeyDown, isEditing],
  );

  const handleCellFocus = useCallback(
    (event: React.FocusEvent<HTMLTableCellElement>) => {
      if (event.target !== cellRef.current) {
        return;
      }
      event.preventDefault();
      setTabIndex(0);
    },
    [setTabIndex],
  );

  const handleCellBlur = useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      if (cellRef.current?.contains(e.relatedTarget as Node)) {
        return;
      }
      setTabIndex(-1);
    },
    [setTabIndex, cellRef],
  );

  const handleCellMouseDown = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement>) => {
      e.preventDefault();
      e.stopPropagation();
      beginEditing();
      moveFocusToCell(cellPosition);
    },
    [beginEditing, cellPosition, moveFocusToCell],
  );

  // Provide focus context to children (e.g., CellStatus) for keyboard accessibility.
  const cellFocusContextValue = useMemo(
    () => ({ isCellFocused: tabIndex === 0 && !isEditing }),
    [tabIndex, isEditing],
  );

  useEffect(() => {
    if (isEditing) {
      localInputRef.current?.focus();
    }
  }, [isEditing]);

  if (!isValidValueType) {
    const { cellProps: _cellProps, ...immutableCellProps } = props;
    return <DataTableBodyImmutableCell {...immutableCellProps} />;
  }

  return (
    <TableBodyCell
      ref={cellRef}
      type="data-table"
      className={classes}
      style={cellProps.style}
      onMouseDown={handleCellMouseDown}
      onKeyDown={handleCellKeyDown}
      tabIndex={tabIndex}
      onFocus={handleCellFocus}
      onBlur={handleCellBlur}
      data-cell-pos={formatCellPositionString(cellPosition)}
    >
      {isEditing ? (
        <>
          <input
            ref={combinedInputRef}
            {...inputProps}
            name={`${cell.column.id}-row-${cellPosition.rowIndex}`}
            aria-label={inputAriaLabel}
            aria-describedby={instructionsId}
            className={styles["data-table-body-cell-input"]}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
          />
        </>
      ) : (
        <CellFocusContext.Provider value={cellFocusContextValue}>
          {children}
          {!isEditing && <DataTableEditableCellEditIcon />}
        </CellFocusContext.Provider>
      )}
      <SrOnly id={instructionsId}>
        {isEditing
          ? "Press Enter or F2 to save cell value."
          : "Press Enter or F2 to edit cell value. Press Escape to cancel."}
      </SrOnly>
    </TableBodyCell>
  );
}

export const DataTableEditableNumberCell = Object.assign(
  forwardRef(DataTableEditableNumberCellInner) as <T>(
    props: DataTableEditableNumberCellProps<T> & {
      ref?: React.Ref<EditableCellImperativeHandle>;
    },
  ) => React.ReactElement,
  { displayName: "DataTableEditableNumberCell" },
);
